/**
 * Budget Create/Edit Form
 *
 * Standalone form for creating or editing a budget. Contains all field state,
 * validation, and submission logic. Can be rendered inside a dialog or inline.
 *
 * Uses react-hook-form + zod for validation. Advanced-panel state is kept
 * as plain useState since it is UI-only (not persisted).
 */

import { Icon, IconKey } from '@/shared/components';
import { FieldCardToggle, type CardToggleOption } from '@/shared/components/form/field-card-toggle';
import { FormFieldCardToggle } from '@/shared/components/form/form-field-card-toggle';
import { FormFieldCategorySelect } from '@/shared/components/form/form-field-category-select';
import { FormFieldCheckbox } from '@/shared/components/form/form-field-checkbox';
import { FormFieldCurrencyInput } from '@/shared/components/form/form-field-currency-input';
import { FormFieldDateInput } from '@/shared/components/form/form-field-date-input';
import { FormFieldSlider } from '@/shared/components/form/form-field-slider';
import { Button } from '@/shared/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import { useButtonStatus } from '@/shared/hooks';
import type { BudgetCreate, BudgetPeriod, BudgetUpdate, BudgetWithProgress } from '@/shared/types';
import { useAuth } from '@/features/auth';
// import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreateBudget, useUpdateBudget } from '../../hooks/use-budgets';
import { budgetFormDefaults, budgetFormSchema, type BudgetFormValues } from '../../schemas/budget-form-schema';

export interface BudgetCreateFormProps {
  /** When provided, the form operates in edit mode. */
  budget?: BudgetWithProgress;
  /** Default category ID to pre-select (for suggestion CTA). */
  defaultCategoryId?: string;
  /** Called after a successful create/update. */
  onSuccess?: () => void;
  /** Called when the user clicks Cancel. */
  onCancel?: () => void;
  /** When changed, resets the form to its initial state. Tie this to dialog open state. */
  resetKey?: number;
}

const PERIOD_OPTIONS: CardToggleOption<BudgetPeriod>[] = [
  { value: 'weekly', label: 'Weekly', icon: IconKey.Calendar },
  { value: 'monthly', label: 'Monthly', icon: IconKey.CalendarDays },
  { value: 'yearly', label: 'Yearly', icon: IconKey.CalendarRange }
];

export function BudgetCreateForm({ budget, defaultCategoryId, onSuccess, onCancel, resetKey }: BudgetCreateFormProps) {
  const isEditing = !!budget;
  const { userId } = useAuth();

  // UI-only state (not form data)
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useForm<BudgetFormValues>({
    resolver: zodResolver(budgetFormSchema),
    defaultValues: budgetFormDefaults,
    mode: 'onTouched'
  });

  // Hooks

  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const { status: saveStatus, execute: executeSave, reset: resetSaveStatus } = useButtonStatus();

  const SCOPE_OPTIONS: CardToggleOption<'household' | 'personal'>[] = [
    { value: 'household', label: 'Household', description: 'Visible to all members', icon: IconKey.Home },
    { value: 'personal', label: 'Just Me', description: 'Only you can see this', icon: IconKey.Lock }
  ];

  // Reset form when resetKey changes (tied to dialog open)
  useEffect(() => {
    resetSaveStatus();
    if (budget) {
      reset({
        categoryId: budget.category_id,
        amount: budget.amount,
        periodType: budget.period_type,
        assignedTo: budget.user_id ?? 'household',
        rollover: budget.rollover,
        notifyAtPercent: budget.notify_at_percent,
        startDate: budget.start_date
      });
      const hasAdvancedValues = budget.rollover || budget.notify_at_percent !== 80;
      setAdvancedOpen(hasAdvancedValues);
    } else {
      reset({
        ...budgetFormDefaults,
        categoryId: defaultCategoryId ?? '',
        startDate: format(new Date(), 'yyyy-MM-dd')
      });
      setAdvancedOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, budget, defaultCategoryId]);

  const onSubmit = async (values: BudgetFormValues) => {
    const result = await executeSave(
      (async () => {
        if (isEditing) {
          const data: BudgetUpdate = {
            amount: values.amount,
            rollover: values.rollover,
            notify_at_percent: values.notifyAtPercent
          };
          return await updateBudget.mutateAsync({ id: budget.id, data });
        } else {
          const targetUserId = values.assignedTo === 'household' ? null : userId;
          const data: BudgetCreate = {
            category_id: values.categoryId,
            amount: values.amount,
            period_type: values.periodType,
            start_date: values.startDate,
            rollover: values.rollover,
            notify_at_percent: values.notifyAtPercent,
            user_id: targetUserId
          };
          return await createBudget.mutateAsync(data);
        }
      })()
    );

    if (result !== undefined) {
      onSuccess?.();
    }
  };

  return (
    <>
      {/* <DevTool control={control} /> */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='grid gap-5 py-5'>
          {/* Category */}
          <FormFieldCategorySelect
            required
            control={control}
            name='categoryId'
            label='Category'
            placeholder='Select a category'
            showUncategorized={false}
            disabled={isEditing}
            aria-label='Budget category'
          />

          {/* Budget Amount */}
          <FormFieldCurrencyInput control={control} name='amount' required id='budget-amount' label='Budget Amount' />

          {/* Period — card toggle */}
          <FormFieldCardToggle<BudgetFormValues, BudgetPeriod>
            required
            control={control}
            name='periodType'
            label='Period'
            options={PERIOD_OPTIONS}
            variant='vertical'
            colorScheme='accent'
            disabled={isEditing}
          />

          {/* Assign To — hidden when editing */}
          {!isEditing && (
            <Controller
              control={control}
              name='assignedTo'
              render={({ field }) => (
                <FieldCardToggle options={SCOPE_OPTIONS} value={field.value} onValueChange={field.onChange} />
              )}
            />
          )}

          {/* Start Date — hidden when editing */}
          {!isEditing && <FormFieldDateInput control={control} name='startDate' id='start-date' label='Start Date' />}

          {/* Advanced Options — Collapsible */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type='button'
                variant='link'
                size='sm'
                className='w-full justify-start text-xs text-muted-foreground hover:text-foreground'
              >
                <Icon
                  icon={IconKey.ChevronDown}
                  size={16}
                  className={`transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`}
                />
                Advanced Options
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className='mt-3'>
              <div className='bg-muted/50 rounded-xl p-4 grid gap-4'>
                {/* Rollover toggle */}
                <FormFieldCheckbox
                  control={control}
                  name='rollover'
                  label='Rollover unused budget'
                  description='Carry unused budget to next period'
                />

                {/* Alert threshold slider */}
                <FormFieldSlider
                  control={control}
                  name='notifyAtPercent'
                  label='Alert at'
                  formatValue={(v) => `${v}%`}
                  min={50}
                  max={100}
                  step={5}
                  minLabel='50%'
                  maxLabel='100%'
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div className='flex flex-col-reverse sm:flex-row sm:justify-end gap-2'>
          <Button type='button' variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button type='submit' disabled={!isValid} status={saveStatus} successText={isEditing ? 'Saved' : 'Created'}>
            {isEditing ? 'Save Changes' : 'Create Budget'}
          </Button>
        </div>
      </form>
    </>
  );
}
